import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { FiRefreshCcw } from "react-icons/fi";
import { FaRadiation, FaLeaf } from "react-icons/fa";
import { FaUserGroup, FaBuilding } from "react-icons/fa6";
import { PiCow } from "react-icons/pi";
import { BsImages, BsFiletypeCsv } from "react-icons/bs";
import GaugeChart from "react-gauge-chart";
import html2canvas from "html2canvas";

const jwt = require("jsonwebtoken");

const greenTheme = createTheme({
  palette: {
    primary: { main: "#028e37" },
  },
});

const SectionBar = ({ title }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "10px 1fr",
      boxShadow: 1,
      bgcolor: "background.paper",
    }}
  >
    <Box sx={{ width: "100%", height: "100%", bgcolor: "primary.main" }} />
    <Typography
      variant="subtitle1"
      fontWeight={600}
      sx={{ py: 1, px: 1, color: "primary.main" }}
    >
      {title}
    </Typography>
  </Box>
);

const StatCard = ({ icon: Icon, value, label }) => (
  <Paper variant="outlined" sx={{ p: 1.5, height: "100%", boxShadow: 1 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ color: "primary.main" }}>
        <Icon style={{ fontSize: 44 }} />
      </Box>
      <Box sx={{ textAlign: "right", flex: 1 }}>
        <Typography variant="h5" fontWeight={900} color="primary.main">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const GaugeSection = ({ id, title, percent }) => {
  // percent is 0–1 (from parent); gauge expects 0–1
  const value = Math.min(1, Math.max(0, Number(percent)));
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: "100%", boxShadow: 1 }}>
      <Typography
        variant="body2"
        textAlign="center"
        color="text.secondary"
        sx={{ mb: 0.5 }}
        title={title}
      >
        {title}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: 140 }}>
        <GaugeChart
          id={id}
          nrOfLevels={20}
          arcWidth={0.3}
          arcPadding={0.02}
          percent={value}
          colors={["#e0e0e0", "#90caf9", "#028e37"]}
          needleColor="#028e37"
          textColor="#333"
          formatTextValue={(val) => `${Math.min(100, Math.round(Number(val) * 100))}%`}
          hideText={false}
        />
      </Box>
    </Paper>
  );
};

const ChartActions = ({ onImage, onCsv }) => (
  <Box sx={{ display: "flex", gap: 0.5 }}>
    <IconButton size="small" onClick={onImage} color="primary" title="Download image">
      <BsImages size={20} />
    </IconButton>
    <IconButton size="small" onClick={onCsv} color="primary" title="Download CSV">
      <BsFiletypeCsv size={20} />
    </IconButton>
  </Box>
);

export default function NewStats() {
  const [refresh, setRefresh] = useState(false);
  const [projects, setProjects] = useState(null);
  const [data, setData] = useState(null);

  const p1ref = useRef(null);
  const p2ref = useRef(null);
  const p3ref = useRef(null);
  const p4ref = useRef(null);
  const p5ref = useRef(null);
  const p6ref = useRef(null);

  const token = localStorage.getItem("gdfhgfhtkn");
  const decoded = token ? jwt.decode(token) : null;
  const level = decoded?.Level ? decoded.Level.replace(" ", "") : null;
  const levelname = decoded?.LevelName;

  useEffect(() => {
    if (!level || !levelname) return;
    fetch(`/api/farmerdetails/all/stats/${level}/${levelname}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then(setData)
      .catch(() => {});
  }, [level, levelname, refresh]);

  useEffect(() => {
    if (!level || !levelname) return;
    fetch(`/api/farmerdetails/charts/${level}/${levelname}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then(setProjects)
      .catch(() => {});
  }, [level, levelname, refresh]);

  const handleDownloadImage = async (printRef) => {
    const element = printRef?.current;
    if (!element) return;
    const canvas = await html2canvas(element);
    const dataUrl = canvas.toDataURL("image/jpg");
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = "image.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const saveData = (arr) => {
    if (!arr?.length) return;
    const rows = [Object.keys(arr[0]), ...arr.map((item) => Object.values(item))];
    const csvContent =
      "data:text/csv;charset=utf-8," +
      rows.map((e) => e.join(",")).join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Backend returns counts; ratio = part/whole. If ratio > 1, backend may be sending 0–100 percentage — normalize to 0–1 for gauge.
  const farmersTotal = Number(data?.Farmers?.[0]?.total ?? 0) || 1;
  const groupsTotal =
    Number(data?.CIG?.[0]?.total ?? 0) +
    Number(data?.SACCO?.[0]?.total ?? 0) +
    Number(data?.PO?.[0]?.total ?? 0);
  const rawFarmersInGroups = groupsTotal / farmersTotal;
  const farmersInGroupsPercent =
    rawFarmersInGroups > 1
      ? Math.min(1, rawFarmersInGroups / 100)
      : Math.min(1, rawFarmersInGroups);

  const totalA = Number(data?.TotalA?.[0]?.total ?? 0) || 1;
  const landInUse =
    Number(data?.LivestockA?.[0]?.total ?? 0) +
    Number(data?.CropA?.[0]?.total ?? 0);
  const rawLandInUse = landInUse / totalA;
  const landInUsePercent =
    rawLandInUse > 1 ? Math.min(1, rawLandInUse / 100) : Math.min(1, rawLandInUse);

  const pieSeries = (arr) =>
    (arr || []).map((d, i) => ({
      id: i,
      value: Number(d.value) || 0,
      label: d.name || "",
    }));

  const barSeries = (arr) =>
    arr?.length
      ? [{ data: (arr || []).map((d) => Number(d.value) || 0) }]
      : [{ data: [] }];
  const barXAxis = (arr) =>
    arr?.length
      ? [{ data: (arr || []).map((d) => d.name || ""), scaleType: "band" }]
      : [{ data: [], scaleType: "band" }];

  return (
    <ThemeProvider theme={greenTheme}>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          p: 2,
          overflow: "auto",
          bgcolor: "grey.50",
        }}
      >
        {/* Welcome */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={560} color="primary.main">
          Farmers Portal
        </Typography>
        <IconButton
          onClick={() => setRefresh((r) => !r)}
          sx={{ bgcolor: "background.paper", "&:hover": { bgcolor: "grey.100" } }}
        >
          <FiRefreshCcw size={24} />
        </IconButton>
      </Box>

      {/* Farmer Groups – full width, same structure as Value Chains */}
      <Box sx={{ mb: 2 }}>
        <SectionBar title="Farmer Groups" />
        <Paper variant="outlined" sx={{ p: 1.5, mt: 1, boxShadow: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 2,
            }}
          >
            <StatCard
              icon={FaUserGroup}
              value={data?.CIG?.[0]?.total?.toFixed(0) ?? 0}
              label="Common Interest Groups"
            />
            <StatCard
              icon={FaRadiation}
              value={data?.PO?.[0]?.total?.toFixed(0) ?? 0}
              label="Producer Organizations"
            />
            <StatCard
              icon={FaBuilding}
              value={data?.SACCO?.[0]?.total?.toFixed(0) ?? 0}
              label="SACCO's"
            />
            <GaugeSection
              id="gauge-farmers-in-groups"
              title="Farmers in groups"
              percent={farmersInGroupsPercent}
            />
          </Box>
        </Paper>
      </Box>

      {/* Land Acreage – full width, same structure as Value Chains */}
      <Box sx={{ mb: 2 }}>
        <SectionBar title="Land Acreage" />
        <Paper variant="outlined" sx={{ p: 1.5, mt: 1, boxShadow: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 2,
            }}
          >
            <StatCard
              icon={PiCow}
              value={data?.LivestockA?.[0]?.total?.toFixed(0) ?? 0}
              label="Livestock Acreage"
            />
            <StatCard
              icon={FaLeaf}
              value={data?.CropA?.[0]?.total?.toFixed(0) ?? 0}
              label="Crop Acreage"
            />
            <GaugeSection
              id="gauge-land-in-use"
              title="Land in use"
              percent={landInUsePercent}
            />
          </Box>
        </Paper>
      </Box>

      {/* Distribution and status: 3 pie charts in one row */}
      <Box sx={{ mb: 2 }}>
        <SectionBar title="Distribution and status" />
        <Box
          sx={{
            mt: 1,
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 2,
          }}
        >
          <Paper ref={p1ref} variant="outlined" sx={{ p: 1.5, boxShadow: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Farming Type
              </Typography>
              <ChartActions
                onImage={() => handleDownloadImage(p1ref)}
                onCsv={() => saveData(projects?.Type)}
              />
            </Box>
            {projects?.Type?.length > 0 && (
              <PieChart
                series={[
                  {
                    data: pieSeries(projects.Type),
                    innerRadius: 30,
                    outerRadius: 80,
                    paddingAngle: 2,
                  },
                ]}
                height={220}
                slotProps={{ legend: { hidden: false } }}
              />
            )}
          </Paper>
          <Paper ref={p2ref} variant="outlined" sx={{ p: 1.5, boxShadow: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Age Distribution
              </Typography>
              <ChartActions
                onImage={() => handleDownloadImage(p2ref)}
                onCsv={() => saveData(projects?.Year)}
              />
            </Box>
            {projects?.Year?.length > 0 && (
              <PieChart
                series={[
                  {
                    data: pieSeries(projects.Year),
                    innerRadius: 30,
                    outerRadius: 80,
                    paddingAngle: 2,
                  },
                ]}
                height={220}
                slotProps={{ legend: { hidden: false } }}
              />
            )}
          </Paper>
          <Paper ref={p3ref} variant="outlined" sx={{ p: 1.5, boxShadow: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Gender Distribution
              </Typography>
              <ChartActions
                onImage={() => handleDownloadImage(p3ref)}
                onCsv={() => saveData(projects?.Expense)}
              />
            </Box>
            {projects?.Expense?.length > 0 && (
              <PieChart
                series={[
                  {
                    data: pieSeries(projects.Expense),
                    innerRadius: 30,
                    outerRadius: 80,
                    paddingAngle: 2,
                  },
                ]}
                height={220}
                slotProps={{ legend: { hidden: false } }}
              />
            )}
          </Paper>
        </Box>
      </Box>

      {/* Sub county Distribution – same structure as Value Chains (edge to edge) */}
      <Box sx={{ mb: 2 }}>
        <SectionBar title="Sub county Distribution" />
        <Paper ref={p4ref} variant="outlined" sx={{ p: 1.5, mt: 1, boxShadow: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
            <ChartActions
              onImage={() => handleDownloadImage(p4ref)}
              onCsv={() => saveData(projects?.SubCounty)}
            />
          </Box>
          {projects?.SubCounty?.length > 0 && (
            <BarChart
              xAxis={barXAxis(projects.SubCounty)}
              series={barSeries(projects.SubCounty)}
              height={280}
              margin={{ top: 10, right: 10, bottom: 30, left: 40 }}
            />
          )}
        </Paper>
      </Box>

      {/* Ward distribution – same structure as Value Chains (edge to edge) */}
      <Box sx={{ mb: 2 }}>
        <SectionBar title="Ward distribution" />
        <Paper ref={p5ref} variant="outlined" sx={{ p: 1.5, mt: 1, boxShadow: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
            <ChartActions
              onImage={() => handleDownloadImage(p5ref)}
              onCsv={() => saveData(projects?.Ward)}
            />
          </Box>
          {projects?.Ward?.length > 0 && (
            <BarChart
              xAxis={barXAxis(projects.Ward)}
              series={barSeries(projects.Ward)}
              height={280}
              margin={{ top: 10, right: 10, bottom: 30, left: 40 }}
            />
          )}
        </Paper>
      </Box>

      {/* Value Chains */}
      <Box>
        <SectionBar title="Value Chains" />
        <Paper ref={p6ref} variant="outlined" sx={{ p: 1.5, mt: 1, boxShadow: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
            <ChartActions
              onImage={() => handleDownloadImage(p6ref)}
              onCsv={() => saveData(projects?.Department)}
            />
          </Box>
          {projects?.Department?.length > 0 && (
            <BarChart
              xAxis={barXAxis(projects.Department)}
              series={barSeries(projects.Department)}
              height={280}
            />
          )}
        </Paper>
      </Box>
      </Box>
    </ThemeProvider>
  );
}
